/* ==========================================
   WEB PUSH NOTIFICATIONS & PWA INSTALL MODULE
   ========================================== */

let deferredInstallPrompt = null;

export function initPwaAndNotifications() {
  // 1. Register Service Worker for PWA with a build-busted URL so stale deploys
  // do not keep serving the old HTML/JS bundle after a production update.
  if ('serviceWorker' in navigator) {
    window.addEventListener('load', async () => {
      try {
        const buildVersion = (() => {
          const moduleScripts = document.querySelectorAll('script[type="module"]');
          for (const script of moduleScripts) {
            const src = script.getAttribute('src') || '';
            const match = src.match(/app\.[^.]+\.js(?:\?v=([A-Za-z0-9]+))?/);
            if (match) return match[1] || 'build';
          }
          return 'build-' + Date.now();
        })();

        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const registration of registrations) {
          const swUrl = new URL(registration.active?.scriptURL || registration.scriptURL || '/sw.js', location.href);
          if (swUrl.searchParams.get('v') !== buildVersion) {
            await registration.unregister();
          }
        }

        const registration = await navigator.serviceWorker.register(`/sw.js?v=${encodeURIComponent(buildVersion)}`, { scope: '/' });
        console.log('[PWA] ServiceWorker registered with scope:', registration.scope, 'version:', buildVersion);
      } catch (err) {
        console.warn('[PWA] ServiceWorker registration failed:', err);
      }
    });
  }

  // 2. Listen for PWA Install Prompt
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredInstallPrompt = e;
    const installBanner = document.getElementById('pwaInstallBanner');
    if (installBanner) {
      installBanner.classList.remove('hidden');
    }
  });

  initializeForegroundMessaging();

  document.querySelectorAll('[data-notification-role]').forEach((button) => {
    if (button.dataset.notificationsBound === 'true') return;
    button.dataset.notificationsBound = 'true';
    button.addEventListener('click', async () => {
      button.disabled = true;
      const enabled = await requestPushNotificationPermission(null, button.dataset.notificationRole);
      button.disabled = false;
      if (enabled) button.textContent = 'Notifications enabled';
    });
  });
}

async function initializeForegroundMessaging() {
  try {
    await window.ensureFirebaseReady?.();
    const messagingSdk = await import('https://www.gstatic.com/firebasejs/12.18.0/firebase-messaging.js');
    if (!(await messagingSdk.isSupported())) return;
    const messaging = messagingSdk.getMessaging(window.firebaseAppInstance);
    messagingSdk.onMessage(messaging, (payload) => {
      const title = payload.notification?.title || 'What To Eat order update';
      const body = payload.notification?.body || payload.data?.message || 'You have an update.';
      if (typeof window.showNotification === 'function') {
        window.showNotification(`${title}: ${body}`, 'info');
      }
      if (Notification.permission === 'granted') {
        navigator.serviceWorker.ready.then((registration) => registration.showNotification(title, {
          body,
          icon: '/icons/icon-192.png',
          data: { url: payload.data?.url || '/' }
        })).catch(() => {});
      }
    });
  } catch (error) {
    console.warn('[FCM] Push messaging setup warning:', error);
  }
}

export async function requestPushNotificationPermission(userUid = null, role = 'client') {
  if (!('Notification' in window)) {
    console.warn('[Push] Notifications not supported in this browser.');
    return false;
  }

  try {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      console.warn('[Push] Notification permission denied or dismissed.');
      return false;
    }

    await window.ensureFirebaseReady?.();
    const auth = window.firebaseAuthRaw || window.firebaseAuthInstance;
    const user = auth?.currentUser;
    if (!user || (userUid && user.uid !== userUid)) {
      throw new Error('Sign in before enabling notifications.');
    }
    if (!navigator.serviceWorker) {
      throw new Error('This browser does not support background notifications.');
    }

    const messagingSdk = await import('https://www.gstatic.com/firebasejs/12.18.0/firebase-messaging.js');
    if (!(await messagingSdk.isSupported())) {
      throw new Error('Push notifications are not supported on this device.');
    }
    const messaging = messagingSdk.getMessaging(window.firebaseAppInstance);
    const registration = await navigator.serviceWorker.ready;
    const token = await messagingSdk.getToken(messaging, { serviceWorkerRegistration: registration });
    if (!token) throw new Error('Firebase did not provide a device notification token.');

    const idToken = await user.getIdToken();
    const appCheckResult = await window.getFirebaseAppCheckToken();
    const projectId = window.firebaseConfig?.projectId || 'what-to-eat-e5ce9';
    const response = await fetch(`https://us-central1-${projectId}.cloudfunctions.net/registerPushToken`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
        'X-Firebase-AppCheck': appCheckResult.token
      },
      body: JSON.stringify({ token, role })
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok || result.ok !== true) {
      throw new Error(result.error || `Notification setup failed (${response.status}).`);
    }

    if (typeof window.showNotification === 'function') {
      window.showNotification('Device notifications are enabled for order updates.', 'success');
    }
    return true;
  } catch (err) {
    console.error('[Push] Error requesting notification permission:', err);
    if (typeof window.showNotification === 'function') {
      window.showNotification(err.message || 'Could not enable device notifications.', 'error');
    }
    return false;
  }
}

export async function disablePushNotificationPermission(role = 'client') {
  try {
    await window.ensureFirebaseReady?.();
    const auth = window.firebaseAuthRaw || window.firebaseAuthInstance;
    const user = auth?.currentUser;
    if (!user || !navigator.serviceWorker) return false;

    const messagingSdk = await import('https://www.gstatic.com/firebasejs/12.18.0/firebase-messaging.js');
    if (!(await messagingSdk.isSupported())) return false;
    const messaging = messagingSdk.getMessaging(window.firebaseAppInstance);
    const registration = await navigator.serviceWorker.ready;
    const token = await messagingSdk.getToken(messaging, { serviceWorkerRegistration: registration });
    if (!token) return false;

    const idToken = await user.getIdToken();
    const appCheckResult = await window.getFirebaseAppCheckToken();
    const projectId = window.firebaseConfig?.projectId || 'what-to-eat-e5ce9';
    const response = await fetch(`https://us-central1-${projectId}.cloudfunctions.net/registerPushToken`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
        'X-Firebase-AppCheck': appCheckResult.token
      },
      body: JSON.stringify({ token, role, enabled: false })
    });
    if (!response.ok) return false;
    return true;
  } catch (error) {
    console.warn('[Push] Could not remove device notification registration:', error);
    return false;
  }
}

export function promptPwaInstall() {
  if (deferredInstallPrompt) {
    deferredInstallPrompt.prompt();
    deferredInstallPrompt.userChoice.then((choiceResult) => {
      if (choiceResult.outcome === 'accepted') {
        console.log('[PWA] User accepted the install prompt');
      }
      deferredInstallPrompt = null;
      const installBanner = document.getElementById('pwaInstallBanner');
      if (installBanner) {
        installBanner.classList.add('hidden');
      }
    });
  }
}

window.requestPushNotificationPermission = requestPushNotificationPermission;
window.disablePushNotificationPermission = disablePushNotificationPermission;
window.promptPwaInstall = promptPwaInstall;
window.initPwaAndNotifications = initPwaAndNotifications;

// ─── Social Gifting & Meal Request in-app notification listeners ─────────────

/**
 * Listen in real-time for new gift orders addressed to this client.
 * Pops an in-app banner whenever a new PENDING gift arrives.
 *
 * @param {object} db          - Firestore instance (window.db)
 * @param {string} recipientId - UID of the signed-in client
 * @returns {function} unsubscribe
 */
export function listenToGiftNotifications(db, recipientId) {
  if (!db || !recipientId) return () => {};

  // Dynamic import keeps the Firestore SDK import in one place
  const { collection, query, where, orderBy, onSnapshot } = window.__firestoreModular || {};
  if (!collection) {
    console.warn('[listenToGiftNotifications] Firestore modular API not available on window.__firestoreModular');
    return () => {};
  }

  const q = query(
    collection(db, 'giftOrders'),
    where('recipientId', '==', recipientId),
    where('status', '==', 'pending'),
    orderBy('createdAt', 'desc')
  );

  const seen = new Set();

  return onSnapshot(q, (snap) => {
    snap.docChanges().forEach((change) => {
      if (change.type !== 'added') return;
      const id = change.doc.id;
      if (seen.has(id)) return;
      seen.add(id);

      const data = change.doc.data() || {};
      const msg = `🎁 ${data.senderName || 'Someone'} sent you a meal gift from ${data.providerName || 'a restaurant'}!`;

      if (typeof window.showNotification === 'function') {
        window.showNotification(msg, 'info');
      }

      // Dispatch a custom event so the UI can refresh the gift tab badge
      window.dispatchEvent(new CustomEvent('giftOrderReceived', { detail: { id, ...data } }));
    });
  });
}

/**
 * Listen in real-time for open social meal requests targeting this client.
 * Pops an in-app banner when a friend asks them to buy a meal.
 *
 * @param {object} db            - Firestore instance
 * @param {string} targetFriendId - UID of the signed-in client
 * @returns {function} unsubscribe
 */
export function listenToMealRequestNotifications(db, targetFriendId) {
  if (!db || !targetFriendId) return () => {};

  const { collection, query, where, orderBy, onSnapshot } = window.__firestoreModular || {};
  if (!collection) {
    console.warn('[listenToMealRequestNotifications] Firestore modular API not available on window.__firestoreModular');
    return () => {};
  }

  const q = query(
    collection(db, 'socialMealRequests'),
    where('targetFriendId', '==', targetFriendId),
    where('status', '==', 'open'),
    orderBy('createdAt', 'desc')
  );

  const seen = new Set();

  return onSnapshot(q, (snap) => {
    snap.docChanges().forEach((change) => {
      if (change.type !== 'added') return;
      const id = change.doc.id;
      if (seen.has(id)) return;
      seen.add(id);

      const data = change.doc.data() || {};
      const msg = `🍔 ${data.requesterName || 'A friend'} is requesting you buy them a meal from ${data.providerName || 'a restaurant'}!`;

      if (typeof window.showNotification === 'function') {
        window.showNotification(msg, 'info');
      }

      // Dispatch a custom event so the UI can refresh the requests tab badge
      window.dispatchEvent(new CustomEvent('mealRequestReceived', { detail: { id, ...data } }));
    });
  });
}

window.listenToGiftNotifications = listenToGiftNotifications;
window.listenToMealRequestNotifications = listenToMealRequestNotifications;
